// Application dependencies

import { BranchesTable } from "@/interfaces/authorisation/branches.interfaces";
import { CompaniesTable } from "@/interfaces/authorisation/companies.interfaces";
import { ProductsTable } from "@/interfaces/authorisation/products.interfaces";

// Authorisation Enums ---------------------------------------------------------------------------------------------------------------------

export enum AuthorisationFields {
  Active = "active",
  Authorisation = "authorisation",
  Branch = "branch",
  BranchId = "branch_id",
  BranchName = "branch_name",
  Company = "company",
  CompanyId = "company_id",
  CompanyName = "company_name",
  CreatedAt = "created_at",
  Email = "email",
  ExternalRef = "external_ref",
  Id = "id",
  Level = "level",
  Name = "name",
  Product = "product",
  ProductId = "product_id",
  ProductName = "product_name",
  ProductProfile = "product_profile",
  ProductTitle = "product_title",
  Role = "role",
  Roles = "roles",
  Title = "title",
  Type = "type",
  UpdatedAt = "updated_at",
}

export enum AuthorisationLevel {
  ALL = "ALL",
  BRANCH = "BRANCH",
}

export enum AuthorisationRole {
  COMPANY_ADMIN = "COMPANY_ADMIN",
  COMPANY_CLIENT = "COMPANY_CLIENT",
  COMPANY_USER = "COMPANY_USER",
  GUEST = "GUEST",
  PORTAL_ADMIN = "PORTAL_ADMIN",
  PORTAL_USER = "PORTAL_USER",
}

export enum AuthorisationProduct {
  CLOUD_ADMIN = "CLOUD_ADMIN",
  PARTS_PORTAL = "PARTS_PORTAL",
  PORTAL_ADMIN = "PORTAL_ADMIN",
  PORTAL_TEMPLATE = "PORTAL_TEMPLATE",
  SERVICE_PORTAL = "SERVICE_PORTAL",
}

// Authorisation Constants -----------------------------------------------------------------------------------------------------------------

export const AuthorisationNonPortalRoles = [
  AuthorisationRole.COMPANY_ADMIN,
  AuthorisationRole.COMPANY_CLIENT,
  AuthorisationRole.COMPANY_USER,
  AuthorisationRole.GUEST,
];

export const AuthorisationPortalRoles = [
  AuthorisationRole.PORTAL_ADMIN,
  AuthorisationRole.PORTAL_USER,
];

export const CORE_PRODUCT: AuthorisationProduct =
  AuthorisationProduct.CLOUD_ADMIN;

export const AuthorisationProductDetails: Record<
  AuthorisationProduct,
  AuthorisationProductDescription
> = {
  [AuthorisationProduct.CLOUD_ADMIN]: {
    [AuthorisationFields.Name]: AuthorisationProduct.CLOUD_ADMIN,
    [AuthorisationFields.Title]: "Cloud Admin",
  },
  [AuthorisationProduct.PARTS_PORTAL]: {
    [AuthorisationFields.Name]: AuthorisationProduct.PARTS_PORTAL,
    [AuthorisationFields.Title]: "Parts Portal",
  },
  [AuthorisationProduct.PORTAL_ADMIN]: {
    [AuthorisationFields.Name]: AuthorisationProduct.PORTAL_ADMIN,
    [AuthorisationFields.Title]: "Portal Admin",
  },
  [AuthorisationProduct.PORTAL_TEMPLATE]: {
    [AuthorisationFields.Name]: AuthorisationProduct.PORTAL_TEMPLATE,
    [AuthorisationFields.Title]: "Portal Template",
  },
  [AuthorisationProduct.SERVICE_PORTAL]: {
    [AuthorisationFields.Name]: AuthorisationProduct.SERVICE_PORTAL,
    [AuthorisationFields.Title]: "Service Portal",
  },
};

export const AuthorisationProductRoleAccess: AuthorisationProductAccess[] = [
  {
    [AuthorisationFields.Product]: AuthorisationProduct.CLOUD_ADMIN,
    roles: [AuthorisationRole.COMPANY_ADMIN, AuthorisationRole.PORTAL_ADMIN],
  },
  {
    [AuthorisationFields.Product]: AuthorisationProduct.PARTS_PORTAL,
    [AuthorisationFields.Roles]: [
      AuthorisationRole.COMPANY_ADMIN,
      AuthorisationRole.COMPANY_CLIENT,
      AuthorisationRole.COMPANY_USER,
    ],
  },
  {
    [AuthorisationFields.Product]: AuthorisationProduct.PORTAL_ADMIN,
    [AuthorisationFields.Roles]: [
      AuthorisationRole.PORTAL_ADMIN,
      AuthorisationRole.PORTAL_USER,
    ],
  },
  {
    [AuthorisationFields.Product]: AuthorisationProduct.PORTAL_TEMPLATE,
    [AuthorisationFields.Roles]: [AuthorisationRole.PORTAL_ADMIN],
  },
  {
    [AuthorisationFields.Product]: AuthorisationProduct.SERVICE_PORTAL,
    [AuthorisationFields.Roles]: [
      AuthorisationRole.COMPANY_ADMIN,
      AuthorisationRole.COMPANY_CLIENT,
      AuthorisationRole.COMPANY_USER,
    ],
  },
];

// Authorisation Interfaces ----------------------------------------------------------------------------------------------------------------

export interface Authorisation {
  [AuthorisationFields.Active]: boolean;
  [AuthorisationFields.BranchId]: number | null;
  [AuthorisationFields.BranchName]: string | null;
  [AuthorisationFields.CompanyId]: number | null;
  [AuthorisationFields.CompanyName]: string | null;
  [AuthorisationFields.CreatedAt]: string | null;
  [AuthorisationFields.ExternalRef]: string | null;
  [AuthorisationFields.Id]: number;
  [AuthorisationFields.Level]: AuthorisationLevel;
  [AuthorisationFields.ProductId]: number;
  [AuthorisationFields.ProductName]: string | null;
  [AuthorisationFields.ProductTitle]: string | null;
  [AuthorisationFields.ProductProfile]: string | null;
  [AuthorisationFields.Role]: AuthorisationRole;
  [AuthorisationFields.UpdatedAt]: string | null;
}

export interface AuthorisationCrud {
  [AuthorisationFields.Active]: boolean;
  [AuthorisationFields.BranchId]: number | null;
  [AuthorisationFields.CompanyId]: number | null;
  [AuthorisationFields.Email]: string;
  [AuthorisationFields.ExternalRef]: string | null;
  [AuthorisationFields.Id]: number | null;
  [AuthorisationFields.Level]: AuthorisationLevel;
  [AuthorisationFields.ProductId]: number;
  [AuthorisationFields.ProductProfile]: string | null;
  [AuthorisationFields.Role]: AuthorisationRole;
}

export interface AuthorisationDetail {
  [AuthorisationFields.Active]: boolean;
  [AuthorisationFields.Branch]: BranchesTable | null;
  [AuthorisationFields.Company]: CompaniesTable | null;
  [AuthorisationFields.CreatedAt]: string | null;
  [AuthorisationFields.Email]: string | null;
  [AuthorisationFields.ExternalRef]: string | null;
  [AuthorisationFields.Id]: number;
  [AuthorisationFields.Level]: AuthorisationLevel;
  [AuthorisationFields.Product]: ProductsTable | null;
  [AuthorisationFields.Role]: AuthorisationRole;
  [AuthorisationFields.UpdatedAt]: string | null;
}

export interface AuthorisationProductDescription {
  [AuthorisationFields.Name]: AuthorisationProduct;
  [AuthorisationFields.Title]: string;
}

export interface AuthorisationProductAccess {
  [AuthorisationFields.Product]: AuthorisationProduct;
  [AuthorisationFields.Roles]: AuthorisationRole[];
}

export interface AuthorisationRedux {
  [AuthorisationFields.Authorisation]: Authorisation | null;
}
